import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, moneyFull } from '../../lib/api';
import { SectionHeader } from '../../components/UI';
import { DataSourceBadge } from '../../components/sales/DataSourceBadge';
import { AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

// Read the last element of an array without relying on Array.prototype.at,
// which is not available in older Safari (<15.4). A stray .at(-1) here was
// the most likely reason the redesigned Sales Dashboard rendered blank in
// production for users on older browsers while working in preview.
const last = (arr) => (arr && arr.length ? arr[arr.length - 1] : null);

// Sales Dashboard — compact overview per Feb-2026 temporal-role vision.
//
// This page's job: "is everything on track?" in one screen, with click-
// through to Rearview (backward), Console (right now), and Plan (forward).
// Full detail lives on the three destination pages; here we render the
// smallest possible surface that still tells the truth.

function pctFmt(v, digits = 1) {
  if (v == null || Number.isNaN(v)) return '—';
  return `${(v * 100).toFixed(digits)}%`;
}

export default function SalesDashboard() {
  const [ts, setTs] = useState(null);
  const [mtd, setMtd] = useState(null);
  const [plan, setPlan] = useState(null);
  const [rem, setRem] = useState(null);
  const [dash, setDash] = useState(null);   // legacy alert payload
  const [briefing, setBriefing] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get('/sales/timeseries'),                     // truncated closed months
      api.get('/sales/console/mtd').catch(() => ({ data: null })),
      api.get('/sales/plan?basis=original').catch(() => ({ data: null })),
      api.get('/sales/plan/remaining-year?basis=original').catch(() => ({ data: null })),
      api.get('/sales/dashboard').catch(() => ({ data: null })),
      api.get('/sales/dashboard/briefing').catch(() => ({ data: null })),
    ])
      .then(([t, m, p, r, d, b]) => {
        setTs(t.data); setMtd(m.data); setPlan(p.data); setRem(r.data); setDash(d.data); setBriefing(b.data);
      })
      .catch(() => toast.error('Could not load Sales Dashboard.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="label-editorial" data-testid="dashboard-loading">Loading Sales Dashboard…</div>;
  if (!ts) return <div className="label-editorial">No dashboard data.</div>;

  const byKey = Object.fromEntries((ts.metrics || []).map((m) => [m.key, m]));

  // Latest closed-month values for the top-line tiles.
  const revenueSeries = (byKey.revenue?.actual || []).filter((p) => p.value != null);
  const revenueGoal = (byKey.revenue?.goal || []).filter((p) => p.value != null);
  const closeRateSeries = (byKey.close_rate?.actual || []).filter((p) => p.value != null);
  const noShowSeries = (byKey.no_show_rate?.actual || []).filter((p) => p.value != null);

  const ytdActual = revenueSeries.reduce((s, p) => s + (p.value || 0), 0);
  const ytdTarget = revenueGoal.reduce((s, p) => s + (p.value || 0), 0);
  const ytdDelta = ytdTarget ? (ytdActual - ytdTarget) / ytdTarget : null;
  const latestCloseRate = last(closeRateSeries);
  const latestNoShowRate = last(noShowSeries);

  // MTD tile — pull from Console MTD payload directly.
  const mtdRev = mtd?.latest?.mtd_revenue;
  const mtdStretch = mtd?.stretch_target;
  const mtdPacePct = (mtdRev != null && mtdStretch) ? mtdRev / mtdStretch : null;
  // Awaiting-pulse state — the current month's Slack post today looked
  // like a recap of the just-closed month, so we've deliberately left MTD
  // empty until a real current-month pulse arrives.
  const boundaryReclassified = (mtd?.data_quality?.reclassified_boundary_posts || []).length > 0;
  const awaitingPulse = mtdRev == null && boundaryReclassified;

  // Projection tile — from Remaining-Year Outlook.
  const projected = rem?.projected_year_end;
  const projectedDelta = rem?.projected_delta_vs_annual;
  const annualTarget = rem?.annual_target;
  const projectedPct = (projected != null && annualTarget) ? projected / annualTarget : null;

  // Mini-chart data — kept for reference; the current destination-card
  // layout renders no inline charts, so we deliberately don't materialise
  // any derived series here to keep the render path narrow.

  const asOf = ts.last_synced
    ? new Date(ts.last_synced).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  const alerts = dash?.alerts || [];

  return (
    <div className="space-y-6" data-testid="sales-dashboard-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <SectionHeader
          eyebrow="Sales · Dashboard"
          title="Is everything on track?"
          description="A compact overview of yesterday (Rearview), right now (Console), and where we're going (Plan). Click any tile for the detail page. History starts January 2026 · YoY comparisons available January 2027."
        />
        <DataSourceBadge
          source="BHAG · Sales Snapshot · Slack #dailyhighlight"
          asOf={asOf}
          confidence="green"
          testId="dashboard-source-badge"
        />
      </div>

      {/* Status banner row — three health signals across Rearview, Console, Plan */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="dashboard-status-banners">
        {/* Rearview status */}
        <Link to="/sales/rearview" className={`flex items-start gap-2 border-l-4 ${alerts.length > 0 ? 'border-rose-500 hover:bg-rose-50' : 'border-emerald-600 hover:bg-emerald-50'} bg-white p-3 transition-colors`} data-testid="banner-rearview">
          {alerts.length > 0
            ? <AlertTriangle size={16} className="text-rose-600 flex-shrink-0 mt-0.5" />
            : <div className="w-4 h-4 rounded-full bg-emerald-500 flex-shrink-0 mt-0.5" />}
          <div className="flex-1">
            <div className="text-sm text-indie-ink font-semibold">
              {alerts.length > 0 ? `${alerts.length} alert${alerts.length === 1 ? '' : 's'} yesterday` : 'No alerts yesterday'}
            </div>
            <div className="text-[11px] uppercase tracking-[0.14em] text-indie-cobalt font-semibold mt-1">OPEN REARVIEW →</div>
          </div>
        </Link>
        {/* Console status */}
        <Link to="/sales/console" className={`flex items-start gap-2 border-l-4 ${mtdPacePct != null && mtdPacePct >= 0.85 ? 'border-emerald-600 hover:bg-emerald-50' : 'border-amber-500 hover:bg-amber-50'} bg-white p-3 transition-colors`} data-testid="banner-console">
          {mtdPacePct != null && mtdPacePct >= 0.85
            ? <div className="w-4 h-4 rounded-full bg-emerald-500 flex-shrink-0 mt-0.5" />
            : <AlertTriangle size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />}
          <div className="flex-1">
            <div className="text-sm text-indie-ink font-semibold">
              {awaitingPulse
                ? "Waiting on today's #dailyhighlight — this morning's post recapped last month"
                : briefing?.forecast_band?.p50 != null
                ? `Pacing ${briefing.forecast_band.p50 >= (mtdStretch || 0) ? 'ahead' : 'behind'} — projected ${moneyFull(briefing.forecast_band.p50)}`
                : mtdRev != null
                ? `MTD ${moneyFull(mtdRev)} · ${pctFmt(mtdPacePct, 0)} of stretch`
                : 'MTD data not yet available today'}
            </div>
            <div className="text-[11px] uppercase tracking-[0.14em] text-indie-cobalt font-semibold mt-1">OPEN CONSOLE →</div>
          </div>
        </Link>
        {/* Plan status */}
        <Link to="/sales/plan" className={`flex items-start gap-2 border-l-4 ${projectedDelta != null && projectedDelta >= 0 ? 'border-emerald-600 hover:bg-emerald-50' : 'border-amber-500 hover:bg-amber-50'} bg-white p-3 transition-colors`} data-testid="banner-plan">
          {projectedDelta != null && projectedDelta >= 0
            ? <div className="w-4 h-4 rounded-full bg-emerald-500 flex-shrink-0 mt-0.5" />
            : <AlertTriangle size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />}
          <div className="flex-1">
            <div className="text-sm text-indie-ink font-semibold">
              {projectedDelta != null
                ? `${projectedDelta >= 0 ? 'Ahead of plan' : 'Behind plan'} — ${projectedDelta >= 0 ? '+' : ''}${moneyFull(projectedDelta)} projected delta`
                : 'Year-end projection loading'}
            </div>
            <div className="text-[11px] uppercase tracking-[0.14em] text-indie-cobalt font-semibold mt-1">OPEN PLAN →</div>
          </div>
        </Link>
      </div>

      {/* Three destination cards — Rearview / Console / Plan, matching the
          Marketing Intelligence dashboard grammar. Each card owns one clock:
          Rearview = closed months, Console = current month MTD, Plan = full year. */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="dashboard-destination-cards">

        {/* REARVIEW card — last completed month */}
        <Link to="/sales/rearview" className="group block bg-white border-t-4 border-indie-navy p-5 hover:bg-indie-cream/30 transition-colors" data-testid="dest-card-rearview">
          <div className="text-[10px] uppercase tracking-[0.16em] text-indie-cobalt font-semibold">◐ REARVIEW</div>
          <div className="mt-1 text-lg font-semibold text-indie-ink">Last completed month</div>
          <div className="text-[11px] text-indie-ink/60">{ts.last_closed_month || '—'} · cash clock</div>
          <div className="mt-3 text-4xl font-semibold text-indie-ink num-tabular">
            {revenueSeries.length ? moneyFull(last(revenueSeries).value) : '—'}
          </div>
          <div className="mt-1 text-[12px] text-indie-ink/70">
            YTD {moneyFull(ytdActual)}
            {ytdDelta != null && (
              <> · <span className={ytdDelta >= 0 ? 'text-emerald-700' : 'text-rose-600'}>{ytdDelta >= 0 ? '+' : ''}{pctFmt(ytdDelta)}</span> vs plan</>
            )}
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3 text-[11px]">
            <div>
              <div className="uppercase tracking-[0.14em] text-indie-ink/55 font-semibold">Close rate</div>
              <div className="text-lg font-semibold text-indie-ink num-tabular">{latestCloseRate ? pctFmt(latestCloseRate.value) : '—'}</div>
            </div>
            <div>
              <div className="uppercase tracking-[0.14em] text-indie-ink/55 font-semibold">No-show rate</div>
              <div className="text-lg font-semibold text-indie-ink num-tabular">{latestNoShowRate ? pctFmt(latestNoShowRate.value) : '—'}</div>
            </div>
            <div>
              <div className="uppercase tracking-[0.14em] text-indie-ink/55 font-semibold">YTD plan</div>
              <div className="text-lg font-semibold text-indie-ink num-tabular">{moneyFull(ytdTarget)}</div>
            </div>
            <div>
              <div className="uppercase tracking-[0.14em] text-indie-ink/55 font-semibold">YTD delta</div>
              <div className={`text-lg font-semibold num-tabular ${ytdDelta != null && ytdDelta < 0 ? 'text-rose-600' : 'text-emerald-700'}`}>
                {ytdDelta != null ? `${ytdDelta >= 0 ? '+' : ''}${moneyFull(ytdActual - ytdTarget)}` : '—'}
              </div>
            </div>
          </div>
          {plan?.biggest_gap_driver?.metric && (
            <div className="mt-4 bg-indie-cream/60 border-l-2 border-indie-navy/40 px-3 py-2">
              <div className="text-[10px] uppercase tracking-[0.14em] text-indie-ink/60 font-semibold">Biggest funnel gap</div>
              <div className="text-[12px] text-indie-ink mt-0.5">
                {plan.biggest_gap_driver.metric} at {plan.biggest_gap_driver.actual_display || plan.biggest_gap_driver.actual}
                {' '}vs target {plan.biggest_gap_driver.target_display || plan.biggest_gap_driver.target}
              </div>
            </div>
          )}
          <div className="mt-4 text-[11px] uppercase tracking-[0.14em] text-indie-cobalt font-semibold group-hover:underline">
            OPEN REARVIEW →
          </div>
        </Link>

        {/* CONSOLE card — this month MTD */}
        <Link to="/sales/console" className="group block bg-white border-t-4 border-indie-navy p-5 hover:bg-indie-cream/30 transition-colors" data-testid="dest-card-console">
          <div className="text-[10px] uppercase tracking-[0.16em] text-indie-cobalt font-semibold">◉ CONSOLE</div>
          <div className="mt-1 text-lg font-semibold text-indie-ink">This month · MTD</div>
          <div className="text-[11px] text-indie-ink/60">
            {awaitingPulse
              ? <>Awaiting first {(mtd?.month_label || '').split(' ')[0] || 'this-month'} post · Slack #dailyhighlight</>
              : <>Day {mtd?.latest?.day_of_month || mtd?.latest?.day || '—'} of month · Slack #dailyhighlight</>}
          </div>
          <div className="mt-3 text-4xl font-semibold text-indie-ink num-tabular">
            {mtdRev != null ? moneyFull(mtdRev) : '—'}
          </div>
          <div className="mt-1 text-[12px] text-indie-ink/70">
            {awaitingPulse
              ? <>This morning&#39;s post was a recap of {mtd?.data_quality?.reclassified_boundary_posts?.[0]?.prior_month_label || 'last month'} — new-month MTD will populate once the next post lands.</>
              : briefing?.forecast_band?.p50 != null
              ? <>Projected month-end <span className="font-semibold text-indie-ink">{moneyFull(briefing.forecast_band.p50)}</span> · range {moneyFull(briefing.forecast_band.p10)}–{moneyFull(briefing.forecast_band.p90)}</>
              : mtdStretch != null && <>Stretch {moneyFull(mtdStretch)}</>}
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3 text-[11px]">
            <div>
              <div className="uppercase tracking-[0.14em] text-indie-ink/55 font-semibold">Sales MTD</div>
              <div className="text-lg font-semibold text-indie-ink num-tabular">{mtd?.funnel_mtd?.won ?? '—'}</div>
            </div>
            <div>
              <div className="uppercase tracking-[0.14em] text-indie-ink/55 font-semibold">Calls booked</div>
              <div className="text-lg font-semibold text-indie-ink num-tabular">{mtd?.funnel_mtd?.booked ?? '—'}</div>
            </div>
            <div>
              <div className="uppercase tracking-[0.14em] text-indie-ink/55 font-semibold">Held</div>
              <div className="text-lg font-semibold text-indie-ink num-tabular">{mtd?.funnel_mtd?.held ?? '—'}</div>
            </div>
            <div>
              <div className="uppercase tracking-[0.14em] text-indie-ink/55 font-semibold">Workdays left</div>
              <div className="text-lg font-semibold text-indie-ink num-tabular">{mtd?.latest?.workdays_left ?? '—'}</div>
            </div>
          </div>
          {briefing?.rep_trend?.length > 0 && (() => {
            const sorted = briefing.rep_trend
              .filter((r) => r.latest?.rolling_avg != null)
              .sort((a, b) => a.latest.rolling_avg - b.latest.rolling_avg);
            const lowest = sorted[0];
            if (!lowest) return null;
            return (
              <div className="mt-4 bg-indie-cream/60 border-l-2 border-indie-navy/40 px-3 py-2">
                <div className="text-[10px] uppercase tracking-[0.14em] text-indie-ink/60 font-semibold">Coaching signal</div>
                <div className="text-[12px] text-indie-ink mt-0.5">
                  {lowest.rep} lowest 4-wk scorecard at <span className="font-semibold">{lowest.latest.rolling_avg.toFixed(2)}</span> (n={lowest.latest.n})
                </div>
              </div>
            );
          })()}
          <div className="mt-4 text-[11px] uppercase tracking-[0.14em] text-indie-cobalt font-semibold group-hover:underline">
            OPEN CONSOLE →
          </div>
        </Link>

        {/* PLAN card — the year */}
        <Link to="/sales/plan" className="group block bg-white border-t-4 border-indie-navy p-5 hover:bg-indie-cream/30 transition-colors" data-testid="dest-card-plan">
          <div className="text-[10px] uppercase tracking-[0.16em] text-indie-cobalt font-semibold">◎ PLAN</div>
          <div className="mt-1 text-lg font-semibold text-indie-ink">The year</div>
          <div className="text-[11px] text-indie-ink/60">Original plan · past actuals + remaining BHAG</div>
          <div className="mt-3 text-4xl font-semibold text-indie-ink num-tabular">
            {projected != null ? moneyFull(projected) : '—'}
          </div>
          <div className="mt-1 text-[12px] text-indie-ink/70">
            {projectedDelta != null && (
              <><span className={projectedDelta >= 0 ? 'text-emerald-700' : 'text-rose-600'}>{projectedDelta >= 0 ? '+' : ''}{moneyFull(projectedDelta)}</span> vs original plan</>
            )}
            {projectedPct != null && <> · <span className="font-semibold text-indie-ink">{pctFmt(projectedPct, 1)}</span> of plan</>}
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3 text-[11px]">
            <div>
              <div className="uppercase tracking-[0.14em] text-indie-ink/55 font-semibold">Original plan</div>
              <div className="text-lg font-semibold text-indie-ink num-tabular">{moneyFull(annualTarget)}</div>
            </div>
            <div>
              <div className="uppercase tracking-[0.14em] text-indie-ink/55 font-semibold">Actual YTD</div>
              <div className="text-lg font-semibold text-indie-ink num-tabular">{moneyFull(ytdActual)}</div>
            </div>
            <div>
              <div className="uppercase tracking-[0.14em] text-indie-ink/55 font-semibold">Closed months</div>
              <div className="text-lg font-semibold text-indie-ink num-tabular">{rem?.closed_months ?? '—'}</div>
            </div>
            <div>
              <div className="uppercase tracking-[0.14em] text-indie-ink/55 font-semibold">Lift needed</div>
              <div className="text-lg font-semibold text-indie-ink num-tabular">
                {rem?.lift_needed_pct != null ? `${(rem.lift_needed_pct * 100).toFixed(1)}%` : '—'}
              </div>
            </div>
          </div>
          {rem?.remaining_months != null && (
            <div className="mt-4 bg-indie-cream/60 border-l-2 border-indie-navy/40 px-3 py-2">
              <div className="text-[10px] uppercase tracking-[0.14em] text-indie-ink/60 font-semibold">Remaining year</div>
              <div className="text-[12px] text-indie-ink mt-0.5">
                {rem.remaining_months} month{rem.remaining_months === 1 ? '' : 's'} left
                {rem.lift_needed_pct != null && (
                  <> · need <span className="font-semibold">{(rem.lift_needed_pct * 100).toFixed(1)}%</span> lift to hit original plan</>
                )}
              </div>
            </div>
          )}
          <div className="mt-4 text-[11px] uppercase tracking-[0.14em] text-indie-cobalt font-semibold group-hover:underline">
            OPEN PLAN →
          </div>
        </Link>
      </div>

      {/* Ownership signal footer — one synthesized read across the three cards */}
      {briefing?.read_of_the_day?.length > 0 && (
        <div className="border-l-4 border-indie-navy bg-indie-cream/50 p-4" data-testid="dashboard-ownership-signal">
          <div className="flex items-start justify-between flex-wrap gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-[0.14em] text-indie-ink/60 font-semibold">Ownership signal</div>
              <div className="mt-1 text-sm text-indie-ink">
                {briefing.read_of_the_day[0].text}
                {briefing.read_of_the_day[1] && <> · {briefing.read_of_the_day[1].text}</>}
              </div>
            </div>
            <Link to="/sales/console" className="text-[11px] uppercase tracking-[0.14em] text-indie-cobalt font-semibold self-end">
              CONSOLE →
            </Link>
          </div>
        </div>
      )}

      <div className="text-[11px] text-indie-ink/50 pt-2 border-t border-indie-ink/10">
        Data cutoff: closed through <span className="font-mono">{ts.last_closed_month}</span> · MTD through yesterday · Cash clock unless otherwise noted.
      </div>
    </div>
  );
}
